import React from "react";
import { Navigate } from "react-router-dom";
import { useAdminAuth } from "../../hooks/useAdminAuth";
import Loading from "../common/Loading";
import { Alert, AlertDescription } from "../ui/alert";
import { AlertTriangle } from "lucide-react";

interface AdminProtectedRouteProps {
  children: React.ReactNode;
}

export const AdminProtectedRoute: React.FC<AdminProtectedRouteProps> = ({
  children,
}) => {
  const { isAdmin, isLoading, error } = useAdminAuth();

  // Debug logging
  console.log('[AdminProtectedRoute] Admin check:', { isAdmin, isLoading, error });

  // Show loading state while checking admin status
  if (isLoading) {
    console.log('[AdminProtectedRoute] Still loading admin check...');
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loading />
      </div>
    );
  }

  // Show error state if there's an error (but don't redirect - let user see the error)
  if (error) {
    console.error('[AdminProtectedRoute] Admin check error:', error);
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {error}
            <br />
            <span className="text-sm text-muted-foreground mt-2 block">
              If you believe you should have admin access, please contact support.
            </span>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Only redirect if we're sure the user is not an admin (after loading completes)
  if (!isAdmin && !isLoading) {
    console.warn('[AdminProtectedRoute] User is not an admin, redirecting to dashboard');
    return <Navigate to="/dashboard" replace />;
  }

  console.log('[AdminProtectedRoute] User is admin, rendering admin portal');
  return <>{children}</>;
};
